import { useEffect, useState } from "react";
import { getAccessToken } from "../../storage/storage";
import axios from "axios";
import { FiRefreshCw, FiChevronDown, FiChevronUp, FiCheckCircle } from "react-icons/fi";
import { MdOutlineSync } from "react-icons/md";

const STATUS_STYLES = {
  success: "bg-green-100 text-green-700",
  completed: "bg-green-100 text-green-700",
  failed: "bg-red-100 text-red-700",
  error: "bg-red-100 text-red-700",
  running: "bg-blue-100 text-blue-700",
  pending: "bg-yellow-100 text-yellow-700",
  partial: "bg-orange-100 text-orange-700",
};

const getStatusStyle = (status) => {
  if (!status) return "bg-gray-100 text-gray-600";
  return STATUS_STYLES[status.toLowerCase()] || "bg-gray-100 text-gray-600";
};

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatDuration = (seconds) => {
  if (seconds == null) return "—";
  if (seconds < 1) return `${Math.round(seconds * 1000)}ms`;
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  return `${Math.floor(seconds / 60)}m ${Math.round(seconds % 60)}s`;
};

const CronLogSkeleton = () =>
  Array.from({ length: 6 }).map((_, i) => (
    <tr key={i} className="animate-pulse border-b border-gray-100">
      {Array.from({ length: 7 }).map((_, j) => (
        <td key={j} className="px-4 py-4">
          <div className="h-3 bg-gray-200 rounded-full w-20" />
        </td>
      ))}
    </tr>
  ));

export default function CronJobLogs() {
  const token = getAccessToken();
  const BASE_URL = process.env.REACT_APP_BASE_URL;

  const [logs, setLogs] = useState([]);
  const [lastSuccessfulSync, setLastSuccessfulSync] = useState(null);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("All");
  const [expandedRow, setExpandedRow] = useState(null);

  const fetchLogs = () => {
    setLoading(true);
    axios
      .get(`${BASE_URL}/api/webhooks/lodgify/sync-logs/`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      })
      .then((res) => {
        setLogs(res.data.results || []);
        setLastSuccessfulSync(res.data.last_successful_sync || null);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching sync logs:", err);
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchLogs();
  }, []);

  const statusOptions = ["All", "Success", "Failed", "Running", "Pending", "Error", "Partial"];

  const filteredLogs = logs.filter((log) =>
    statusFilter === "All" ||
    log.status?.toLowerCase() === statusFilter.toLowerCase()
  );

  return (
    <div className="p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-[#FFF5CE] rounded-lg">
            <MdOutlineSync className="text-2xl text-[#586989]" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-[#586989]">Auto-Scheduled Task Logs</h1>
            <p className="text-xs text-gray-500">Lodgify scheduled sync activity</p>
          </div>
        </div>
        <button
          onClick={fetchLogs}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-[#586989] border border-[#586989] rounded-lg hover:bg-[#FFF5CE] transition-colors disabled:opacity-50 self-start sm:self-auto"
        >
          <FiRefreshCw className={loading ? "animate-spin" : ""} />
          Refresh
        </button>
      </div>

      {/* Last successful sync banner */}
      {lastSuccessfulSync && (
        <div className="flex items-center gap-2 mb-5 px-4 py-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">
          <FiCheckCircle className="shrink-0" />
          <span>Last successful sync: <span className="font-semibold">{formatDate(lastSuccessfulSync)}</span></span>
        </div>
      )}

      {/* Stats bar */}
      {!loading && logs.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
          {[
            { label: "Total Runs", value: logs.length, color: "text-gray-700" },
            {
              label: "Successful",
              value: logs.filter((l) => ["success", "completed"].includes(l.status?.toLowerCase())).length,
              color: "text-green-600",
            },
            {
              label: "Failed",
              value: logs.filter((l) => ["failed", "error"].includes(l.status?.toLowerCase())).length,
              color: "text-red-500",
            },
          ].map((stat) => (
            <div key={stat.label} className="bg-white border border-gray-200 rounded-lg px-4 py-3">
              <p className="text-xs text-gray-400">{stat.label}</p>
              <p className={`text-xl font-bold ${stat.color}`}>{stat.value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Filter */}
      <div className="flex gap-3 mb-5">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#586989]/30 bg-white"
        >
          {statusOptions.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200 text-gray-600 text-left">
                <th className="px-4 py-3 font-semibold">#</th>
                <th className="px-4 py-3 font-semibold">Sync Type</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 font-semibold">Started At</th>
                <th className="px-4 py-3 font-semibold">Details</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <CronLogSkeleton />
              ) : filteredLogs.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-16 text-center text-gray-400">
                    <MdOutlineSync className="text-4xl mx-auto mb-2 opacity-30" />
                    <p className="text-sm">No sync logs found</p>
                  </td>
                </tr>
              ) : (
                filteredLogs.map((log) => {
                  const isExpanded = expandedRow === log.id;
                  return (
                    <>
                      <tr
                        key={log.id}
                        className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                      >
                        <td className="px-4 py-3 text-gray-400 text-xs">{log.id}</td>
                        <td className="px-4 py-3 text-gray-700">
                          {log.job_name_display || "—"}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-block px-2 py-1 rounded-full text-xs font-semibold ${getStatusStyle(log.status)}`}>
                            {log.status_display || log.status || "unknown"}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">
                          {formatDate(log.started_at)}
                        </td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => setExpandedRow(isExpanded ? null : log.id)}
                            className="flex items-center gap-1 text-[#586989] hover:text-[#3d4f63] text-xs font-medium"
                          >
                            {isExpanded ? <FiChevronUp /> : <FiChevronDown />}
                            {isExpanded ? "Hide" : "View"}
                          </button>
                        </td>
                      </tr>

                      {isExpanded && (
                        <tr key={`${log.id}-expanded`}>
                          <td colSpan={7} className="bg-gray-50 border-b border-gray-200 px-6 py-5">
                            <div className="space-y-3 max-w-lg">
                              <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider">Run Details</h4>
                              <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-2 text-xs">
                                <InfoRow label="User" value={log.user_email} />
                                <InfoRow label="Sync Type" value={log.job_name_display || log.sync_type} />
                                <InfoRow label="Started At" value={formatDate(log.started_at)} />
                                <InfoRow label="Completed At" value={formatDate(log.completed_at)} />
                                <InfoRow label="Duration" value={formatDuration(log.duration_seconds)} />
                              </div>

                              {log.error_message && (
                                <>
                                  <h4 className="text-xs font-bold text-red-500 uppercase tracking-wider pt-1">Error</h4>
                                  <pre className="text-xs text-red-700 bg-red-50 border border-red-100 rounded-lg p-3 overflow-x-auto whitespace-pre-wrap">
                                    {log.error_message}
                                  </pre>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {!loading && filteredLogs.length > 0 && (
          <div className="px-4 py-3 border-t border-gray-100 text-xs text-gray-400">
            Showing {filteredLogs.length} of {logs.length} run{logs.length !== 1 ? "s" : ""}
          </div>
        )}
      </div>
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div className="flex justify-between gap-2">
      <span className="text-gray-400 shrink-0">{label}</span>
      <span className="text-gray-700 text-right font-medium">{value ?? "—"}</span>
    </div>
  );
}
